/**
 * AdminAvatar — circular admin avatar with initials fallback.
 *
 * Used on the list (size 36) and on the edit page rail (size 96).
 *
 * Three states:
 *   1. URL present and loads → photo
 *   2. URL present but fails to load (404, blocked, etc.) → initials
 *      on a deterministically-tinted disk (caught via <img onError>)
 *   3. URL missing / null / "null" / "undefined" / empty / whitespace
 *      → initials directly (no load attempt)
 *
 * Why an <img> and not a background-image div? `backgroundImage`
 * silently fails — no event fires, no way to detect the broken URL,
 * and the element just renders blank. We need onError to flip back
 * to the initials state.
 */
import React, { useEffect, useState } from 'react';
import { cn } from '../../../redesign/cn';

const TINTS = ['#75A293', '#C4715B', '#9DA67E', '#5C7E72', '#E58A6A'];

function hasUrl(value) {
  if (value == null) return false;
  const s = String(value).trim();
  if (s === '' || s === 'null' || s === 'undefined') return false;
  return true;
}

function initials(name) {
  if (!name) return '··';
  const w = String(name).trim().split(/\s+/);
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

function tintFor(id) {
  return TINTS[Math.abs(Number(id) || 0) % TINTS.length];
}

export default function AdminAvatar({
  id,
  name,
  image,
  size = 36,
  className,
}) {
  // Track image load failure so we fall back to initials when the
  // URL is set but broken.
  const [broken, setBroken] = useState(!hasUrl(image));

  // Reset broken-state when the URL prop changes.
  useEffect(() => {
    setBroken(!hasUrl(image));
  }, [image]);

  const showInitials = broken;
  const tint = tintFor(id);
  const fontSize = Math.max(10, Math.round(size * 0.34));

  return (
    <span
      aria-hidden
      className={cn(
        'inline-grid place-items-center text-white font-condensed font-bold relative overflow-hidden',
        className,
      )}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        fontSize,
        flexShrink: 0,
        background: tint,
        boxShadow: 'inset 0 0 0 1px #00000010',
      }}
    >
      {/* Initials always rendered so we don't get a flash of nothing
       *  while the image is in-flight; the <img> sits on top until it
       *  errors out. */}
      <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
        {initials(name)}
      </span>
      {!showInitials && (
        <img
          src={image}
          alt=""
          onError={() => setBroken(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}
    </span>
  );
}
